import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Typography, Stack, IconButton, Button, Tooltip,
  CircularProgress, Alert, Chip, Divider,
} from "@mui/material";
import {
  FolderOpen, InsertDriveFile, ArrowBack, Download,
  Close, Refresh, ChevronRight, Home as HomeIcon,
  PictureAsPdf, NavigateNext, CheckCircle, Error as ErrorIcon,
  InfoOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = {
  navy:      "#0a0e27",
  navyDark:  "#0f1233",
  navyLight: "#8fa3e8",
  gold:      "#637cdb",
  offWhite:  "#eef1f8",
};

// ── Utilities ─────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function parseBreadcrumbs(subPrefix) {
  if (!subPrefix) return [];
  return subPrefix.split("/").filter(Boolean);
}

// ── Toast system ──────────────────────────────────────────────────────────────
function ToastItem({ toast, onRemove }) {
  const DURATION = 4200;
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);

  useEffect(() => {
    const step = 100 / (DURATION / 60);
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p - step <= 0) { clearInterval(intervalRef.current); onRemove(toast.id); return 0; }
        return p - step;
      });
    }, 60);
    return () => clearInterval(intervalRef.current);
  }, [toast.id, onRemove]);

  const icons = { success: <CheckCircle sx={{ fontSize: 18 }} />, error: <ErrorIcon sx={{ fontSize: 18 }} />, info: <InfoOutlined sx={{ fontSize: 18 }} /> };
  const colors = { success: "#22c55e", error: "#ef4444", info: BRAND.navyLight };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.92 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.88 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      style={{ marginBottom: 10 }}
    >
      <Box sx={{
        minWidth: 280, maxWidth: 360, borderRadius: "12px", overflow: "hidden",
        bgcolor: "rgba(15,23,42,0.95)", backdropFilter: "blur(20px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <Box sx={{ color: colors[toast.type], mt: 0.1, flexShrink: 0 }}>{icons[toast.type]}</Box>
          <Typography sx={{ fontSize: 13.5, color: "#f1f5f9", flex: 1, lineHeight: 1.5 }}>
            {toast.message}
          </Typography>
          <IconButton size="small" onClick={() => onRemove(toast.id)}
            sx={{ color: "rgba(255,255,255,0.35)", p: 0.25, mt: -0.25,
              "&:hover": { color: "#fff" } }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: "rgba(255,255,255,0.06)", position: "relative" }}>
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.06, ease: "linear" }}
            style={{ height: "100%", background: colors[toast.type], borderRadius: "0 0 0 0" }}
          />
        </Box>
      </Box>
    </motion.div>
  );
}

function ToastContainer({ toasts, removeToast }) {
  return (
    <Box sx={{ position: "fixed", bottom: 28, right: 24, zIndex: 9999, pointerEvents: "none" }}>
      <AnimatePresence>
        {toasts.map((t) => (
          <Box key={t.id} sx={{ pointerEvents: "auto" }}>
            <ToastItem toast={t} onRemove={removeToast} />
          </Box>
        ))}
      </AnimatePresence>
    </Box>
  );
}

// ── Orbital loader ────────────────────────────────────────────────────────────
function OrbitalLoader() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      py: 10, gap: 3 }}>
      <Box sx={{ position: "relative", width: 72, height: 72 }}>
        {/* Outer ring */}
        <Box sx={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "2px solid rgba(10,14,39,0.15)",
        }} />
        {/* Spinning arc */}
        <Box sx={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "2px solid transparent",
          borderTopColor: BRAND.navy,
          "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          animation: "spin 1.1s linear infinite",
        }} />
        {/* Counter-rotating gold arc */}
        <Box sx={{
          position: "absolute", inset: 8, borderRadius: "50%",
          border: "2px solid transparent",
          borderBottomColor: BRAND.gold,
          "@keyframes spinReverse": { to: { transform: "rotate(-360deg)" } },
          animation: "spinReverse 1.6s linear infinite",
        }} />
        {/* Pulsing center orb */}
        <Box sx={{
          position: "absolute", inset: "50%", transform: "translate(-50%,-50%)",
          width: 10, height: 10, borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND.gold}, ${BRAND.navy})`,
          "@keyframes orb": { "0%,100%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" }, "50%": { opacity: 0.5, transform: "translate(-50%,-50%) scale(0.6)" } },
          animation: "orb 1.6s ease-in-out infinite",
        }} />
      </Box>
      <Typography sx={{ fontSize: 13, color: "rgba(10,14,39,0.55)", fontWeight: 600, letterSpacing: "0.08em" }}>
        LOADING REPORTS…
      </Typography>
    </Box>
  );
}

// ── Skeleton cards ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars -- kept for parity with source; not yet wired up
function GridSkeleton({ isDark }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
      {[...Array(6)].map((_, i) => (
        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: i * 0.05 }}>
          <Box sx={{
            height: 96, borderRadius: "14px",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.04)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.07)"}`,
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "-400px 0" },
              "100%": { backgroundPosition: "400px 0" },
            },
            background: isDark
              ? "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 75%)"
              : "linear-gradient(90deg, rgba(10,14,39,0.03) 25%, rgba(10,14,39,0.07) 50%, rgba(10,14,39,0.03) 75%)",
            backgroundSize: "800px 100%",
            animation: "shimmer 1.6s infinite",
          }} />
        </motion.div>
      ))}
    </Box>
  );
}

// ── Folder card ───────────────────────────────────────────────────────────────
function FolderCard({ name, onClick, isDark, border }) {
  const label = name.replace(/\/$/, "");
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Box sx={{
        p: 2.5, borderRadius: "14px",
        border: `1px solid ${border}`,
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
        backdropFilter: "blur(12px)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.15)" : "0 2px 12px rgba(10,14,39,0.04)",
        display: "flex", alignItems: "center", gap: 2,
        transition: "box-shadow 0.25s, border-color 0.25s",
        "&:hover": {
          borderColor: BRAND.gold,
          boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.3)" : "0 8px 28px rgba(10,14,39,0.12)",
        },
      }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #28336b 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(10,14,39,0.3)",
        }}>
          <FolderOpen sx={{ color: BRAND.gold, fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 700,
            color: isDark ? "#f1f5f9" : BRAND.navy,
            wordBreak: "break-word", overflowWrap: "break-word",
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", mt: 0.3, fontWeight: 500 }}>
            Folder
          </Typography>
        </Box>
        <ChevronRight sx={{ color: BRAND.gold, ml: "auto", flexShrink: 0, fontSize: 20 }} />
      </Box>
    </motion.div>
  );
}

// ── File card ─────────────────────────────────────────────────────────────────
function FileCard({ file, onPreview, onDownload, isDark, border }) {
  const [downloading, setDownloading] = useState(false);
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
    >
      <Box sx={{
        p: 2.5, borderRadius: "14px",
        border: `1px solid ${border}`,
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
        backdropFilter: "blur(12px)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.15)" : "0 2px 12px rgba(10,14,39,0.04)",
        transition: "box-shadow 0.25s, border-color 0.25s",
        "&:hover": {
          borderColor: BRAND.gold,
          boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.3)" : "0 8px 28px rgba(10,14,39,0.12)",
        },
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "10px", flexShrink: 0,
            background: "linear-gradient(135deg, #c8322a 0%, #ef4444 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(239,68,68,0.3)",
          }}>
            <PictureAsPdf sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{
              fontSize: 13, fontWeight: 700,
              color: isDark ? "#f1f5f9" : "#1e293b",
              wordBreak: "break-word", overflowWrap: "break-word",
              lineHeight: 1.4,
            }}>
              {file.filename}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
                {formatBytes(file.size_bytes)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: isDark ? "#475569" : "#cbd5e1" }}>·</Typography>
              <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
                {formatDate(file.updated)}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button size="small" variant="outlined" fullWidth onClick={() => onPreview(file)}
            sx={{
              fontSize: 11.5, fontWeight: 700, borderRadius: "8px", py: 0.75,
              textTransform: "none", letterSpacing: "0.02em",
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.15)",
              color: isDark ? "#94a3b8" : "#475569",
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "rgba(10,14,39,0.04)" },
            }}>
            Preview
          </Button>
          <Button size="small" variant="contained" fullWidth
            disabled={downloading}
            startIcon={downloading
              ? <CircularProgress size={12} sx={{ color: "#fff" }} />
              : <Download sx={{ fontSize: 14 }} />}
            onClick={async () => {
              setDownloading(true);
              await onDownload(file);
              setDownloading(false);
            }}
            sx={{
              fontSize: 11.5, fontWeight: 700, borderRadius: "8px", py: 0.75,
              textTransform: "none", letterSpacing: "0.02em",
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
              boxShadow: "0 3px 10px rgba(10,14,39,0.3)",
              "&:hover": { boxShadow: "0 4px 14px rgba(10,14,39,0.45)" },
              "&:disabled": { opacity: 0.6 },
            }}>
            {downloading ? "…" : "Download"}
          </Button>
        </Stack>
      </Box>
    </motion.div>
  );
}

// ── PDF Preview panel ─────────────────────────────────────────────────────────
function PreviewPanel({ file, signedUrl, onClose, isDark, border }) {
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1199,
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
        }}
      />
      <motion.div
        initial={{ x: "100%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(700px, 100vw)", zIndex: 1200,
          display: "flex", flexDirection: "column",
          boxShadow: "-12px 0 60px rgba(0,0,0,0.35)",
        }}
      >
        <Box sx={{
          height: "100%", display: "flex", flexDirection: "column",
          bgcolor: isDark ? "#111827" : "#ffffff",
          borderLeft: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.12)"}`,
        }}>
          {/* Top stripe */}
          <Box sx={{ height: 3, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})`, flexShrink: 0 }} />

          {/* Preview header */}
          <Box sx={{
            px: 3, py: 2.5, flexShrink: 0,
            borderBottom: `1px solid ${border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.03)",
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
                background: "linear-gradient(135deg, #c8322a, #ef4444)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <PictureAsPdf sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 13.5, fontWeight: 700,
                  color: isDark ? "#f1f5f9" : BRAND.navy,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {file.filename}
                </Typography>
                <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
                  {formatBytes(file.size_bytes)} · {formatDate(file.updated)}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {signedUrl && (
                <Tooltip title="Download">
                  <IconButton size="small" component="a" href={signedUrl} download target="_blank"
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b", borderRadius: "8px", p: 0.85,
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(10,14,39,0.1)" },
                    }}>
                    <Download sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close preview">
                <IconButton size="small" onClick={onClose}
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b", borderRadius: "8px", p: 0.85,
                    "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" },
                  }}>
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Preview content */}
          <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
            {signedUrl ? (
              <iframe
                src={signedUrl}
                title={file.filename}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : (
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: "100%", flexDirection: "column", gap: 2,
              }}>
                <OrbitalLoader />
              </Box>
            )}
          </Box>
        </Box>
      </motion.div>
    </>
  );
}

// ── Main LabReports component ─────────────────────────────────────────────────
export default function LabReports() {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const bg      = isDark ? "#080d1a" : "#f0f4ff";
  const sidebar = isDark ? "rgba(11,17,32,0.92)" : "rgba(255,255,255,0.92)";
  const border  = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const [subPrefix,   setSubPrefix]   = useState("");
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [signedUrl,   setSignedUrl]   = useState("");
  const [toasts,      setToasts]      = useState([]);

  const token = user?.token;

  const addToast = useCallback((message, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const browse = useCallback(async (sp = "") => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/my-reports/browse`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { sub_prefix: sp },
      });
      setData(res.data);
      setSubPrefix(sp);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to load reports. Please try again.";
      setError(msg);
      addToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [token, addToast]);

  useEffect(() => { browse(""); }, [browse]);

  const openFolder = (folderRelative) => {
    const sp = subPrefix + folderRelative + (folderRelative.endsWith("/") ? "" : "/");
    browse(sp);
    setPreviewFile(null);
    setSignedUrl("");
  };

  const goUp = () => {
    const parts = subPrefix.split("/").filter(Boolean);
    parts.pop();
    const up = parts.length ? parts.join("/") + "/" : "";
    browse(up);
    setPreviewFile(null);
    setSignedUrl("");
  };

  const getSignedUrl = async (file) => {
    try {
      const res = await axios.get(`${API_BASE}/my-reports/signed-url`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { blob_name: file.blob_name },
      });
      return res.data.url;
    } catch {
      addToast("Could not generate download link. Please try again.", "error");
      return "";
    }
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setSignedUrl("");
    const url = await getSignedUrl(file);
    setSignedUrl(url);
  };

  const handleDownload = async (file) => {
    const url = await getSignedUrl(file);
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      addToast(`Downloading ${file.filename}`, "success");
    }
  };

  const breadcrumbs = parseBreadcrumbs(subPrefix);
  const isClinician = user?.role === "clinician";
  const hasContent  = data && (data.folders.length > 0 || data.files.length > 0);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: bg, overflow: "hidden", position: "relative" }}>

      {/* Animated background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-8%",  left: "-4%",  size: 500, color: isDark ? "rgba(10,14,39,0.14)" : "rgba(10,14,39,0.06)", delay: 0 },
          { top: "55%",  right: "-6%", size: 420, color: isDark ? "rgba(99,124,219,0.06)" : "rgba(99,124,219,0.1)", delay: 1.5 },
          { top: "25%",  left: "48%",  size: 320, color: isDark ? "rgba(10,14,39,0.08)" : "rgba(10,14,39,0.04)", delay: 0.8 },
        ].map((b, i) => (
          <motion.div key={i}
            style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
              width: b.size, height: b.size, borderRadius: "50%",
              background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 9 + i * 2.5, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* ── Header ── */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }} style={{ position: "relative", zIndex: 10, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.92)" : "rgba(0,37,96,0.97)",
          backdropFilter: "blur(16px) saturate(1.4)",
          borderBottom: `2px solid rgba(99,124,219,0.3)`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
        }}>
          <Box sx={{ px: 3, height: 64, display: "flex", alignItems: "center",
            justifyContent: "space-between", maxWidth: 1440, mx: "auto" }}>

            <Box component={Link} to="/home"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Live badge */}
              <Box sx={{
                display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.75,
                bgcolor: "rgba(99,124,219,0.12)", border: "1px solid rgba(99,124,219,0.28)",
                borderRadius: "8px", px: 1.5, py: 0.5,
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.gold,
                  "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                  animation: "pulse 2s infinite",
                }} />
                <Typography sx={{ fontFamily: "Michroma", fontSize: 10.5, fontWeight: 700, color: BRAND.gold, letterSpacing: "0.1em" }}>
                  MY REPORTS
                </Typography>
              </Box>

              <ThemeToggle />

              <Box onClick={() => navigate("/home")}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  color: "rgba(255,255,255,0.6)", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5,
                  borderRadius: "8px", transition: "color 0.2s, background 0.2s",
                  "&:hover": { color: BRAND.gold, bgcolor: "rgba(99,124,219,0.1)" },
                }}>
                <ArrowBack sx={{ fontSize: 15 }} />
                <Box sx={{ display: { xs: "none", sm: "block" } }}>Home</Box>
              </Box>

              <NavUserMenu />
            </Stack>
          </Box>
        </Box>
      </motion.div>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", maxWidth: 1440, mx: "auto", width: "100%", position: "relative", zIndex: 10 }}>

        {/* ── Left sidebar ── */}
        <motion.div
          initial={{ x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
          style={{ display: "flex", flexShrink: 0 }}
        >
          <Box sx={{
            width: 268, flexShrink: 0,
            bgcolor: sidebar,
            backdropFilter: "blur(24px) saturate(1.4)",
            borderRight: `1px solid ${border}`,
            display: { xs: "none", md: "flex" },
            flexDirection: "column", p: 3,
            boxShadow: isDark ? "4px 0 20px rgba(0,0,0,0.2)" : "4px 0 20px rgba(10,14,39,0.04)",
          }}>
            {/* Section header */}
            <Typography sx={{
              fontSize: 10, fontWeight: 800, letterSpacing: "0.18em",
              color: textSub, mb: 2.5,
            }}>STORAGE</Typography>

            {/* Archive info card */}
            <Box sx={{
              p: 2, borderRadius: "12px",
              background: isDark
                ? "linear-gradient(135deg, rgba(10,14,39,0.3), rgba(10,14,39,0.15))"
                : "linear-gradient(135deg, rgba(10,14,39,0.08), rgba(10,14,39,0.04))",
              border: `1px solid ${isDark ? "rgba(10,14,39,0.5)" : "rgba(10,14,39,0.12)"}`,
              mb: 2.5,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: "8px",
                  background: `linear-gradient(135deg, ${BRAND.navy}, #28336b)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FolderOpen sx={{ color: BRAND.gold, fontSize: 15 }} />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
                  {isClinician ? "Clinician Archive" : "Patient Archive"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11.5, color: textSub, lineHeight: 1.6 }}>
                {isClinician
                  ? `Reports for ${user?.practice_name || "your practice"}`
                  : `Reports for ${user?.name || "you"}`}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: border }} />

            <Typography sx={{
              fontSize: 10, fontWeight: 800, letterSpacing: "0.14em",
              color: textSub, mb: 1.5,
            }}>QUICK NAV</Typography>

            {/* All Reports nav item */}
            <Box onClick={() => browse("")}
              sx={{
                display: "flex", alignItems: "center", gap: 1.25,
                px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
                bgcolor: !subPrefix
                  ? (isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.1)")
                  : "transparent",
                border: !subPrefix
                  ? `1px solid ${isDark ? "rgba(10,14,39,0.5)" : "rgba(10,14,39,0.2)"}`
                  : "1px solid transparent",
                "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)" },
                transition: "background 0.18s, border-color 0.18s",
              }}>
              <HomeIcon sx={{ fontSize: 16, color: !subPrefix ? BRAND.navy : textSub }} />
              <Typography sx={{
                fontSize: 13, fontWeight: !subPrefix ? 700 : 500,
                color: !subPrefix ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
              }}>
                All Reports
              </Typography>
              {!subPrefix && (
                <Box sx={{
                  ml: "auto", width: 6, height: 6, borderRadius: "50%",
                  bgcolor: BRAND.gold,
                }} />
              )}
            </Box>

            {/* Breadcrumb nav items */}
            {breadcrumbs.map((crumb, i) => {
              const sp = breadcrumbs.slice(0, i + 1).join("/") + "/";
              const active = sp === subPrefix;
              return (
                <Box key={sp} onClick={() => browse(sp)}
                  sx={{
                    display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0,
                    pl: 1.5 + (i + 1) * 1.5, pr: 1.5, py: 1,
                    borderRadius: "10px", cursor: "pointer",
                    bgcolor: active
                      ? (isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.1)")
                      : "transparent",
                    border: active
                      ? `1px solid ${isDark ? "rgba(10,14,39,0.5)" : "rgba(10,14,39,0.2)"}`
                      : "1px solid transparent",
                    "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)" },
                    transition: "background 0.18s",
                  }}>
                  <FolderOpen sx={{ fontSize: 14, color: active ? BRAND.navy : textSub, mt: "1px", flexShrink: 0 }} />
                  <Typography sx={{
                    fontSize: 12, fontWeight: active ? 700 : 400,
                    color: active ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
                    wordBreak: "break-word", overflowWrap: "break-word", minWidth: 0,
                  }}>
                    {crumb}
                  </Typography>
                </Box>
              );
            })}

            {/* Spacer + disclaimer */}
            <Box sx={{ flex: 1 }} />
            <Box sx={{
              mt: 3, p: 1.75, borderRadius: "10px",
              bgcolor: isDark ? "rgba(99,124,219,0.06)" : "rgba(99,124,219,0.08)",
              border: `1px solid rgba(99,124,219,0.18)`,
            }}>
              <Typography sx={{ fontSize: 10.5, color: isDark ? "rgba(99,124,219,0.7)" : "#78716c", lineHeight: 1.65 }}>
                Reports are end-to-end encrypted and accessible only to you.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Toolbar / breadcrumb bar */}
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Box sx={{
              px: { xs: 2, md: 4 }, py: 2,
              borderBottom: `1px solid ${border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 1, flexShrink: 0,
              bgcolor: isDark ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.6)",
              backdropFilter: "blur(12px)",
            }}>
              {/* Breadcrumb */}
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                <Box onClick={() => browse("")}
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer",
                    color: BRAND.navy, "&:hover": { color: BRAND.gold }, transition: "color 0.18s" }}>
                  <HomeIcon sx={{ fontSize: 18 }} />
                </Box>
                {breadcrumbs.map((crumb, i) => {
                  const sp = breadcrumbs.slice(0, i + 1).join("/") + "/";
                  const isLast = i === breadcrumbs.length - 1;
                  return (
                    <React.Fragment key={sp}>
                      <NavigateNext sx={{ fontSize: 16, color: isDark ? "#475569" : "#cbd5e1" }} />
                      <Typography
                        onClick={() => !isLast && browse(sp)}
                        sx={{
                          fontSize: 13, fontWeight: isLast ? 700 : 500,
                          color: isLast ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
                          cursor: isLast ? "default" : "pointer",
                          "&:hover": isLast ? {} : { color: BRAND.gold },
                          transition: "color 0.18s",
                        }}>
                        {crumb}
                      </Typography>
                    </React.Fragment>
                  );
                })}
              </Stack>

              {/* Actions */}
              <Stack direction="row" spacing={1} alignItems="center">
                {subPrefix && (
                  <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 13 }} />}
                    onClick={goUp}
                    sx={{
                      fontSize: 12, fontWeight: 600, color: textSub, textTransform: "none",
                      borderRadius: "8px", px: 1.5,
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(10,14,39,0.06)" },
                    }}>
                    Up
                  </Button>
                )}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => browse(subPrefix)} disabled={loading}
                    sx={{
                      color: textSub, p: 0.75, borderRadius: "8px",
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(10,14,39,0.08)" },
                    }}>
                    <Refresh sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {data && (
                  <Chip
                    label={`${data.folders.length} folder${data.folders.length !== 1 ? "s" : ""}  ·  ${data.files.length} file${data.files.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      fontSize: 11, height: 24,
                      bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)",
                      border: `1px solid ${border}`, color: textSub,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </motion.div>

          {/* Content area */}
          <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
            {error && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                <Alert severity="error" sx={{ mb: 3, borderRadius: "12px", fontSize: 13 }}
                  onClose={() => setError("")}>
                  {error}
                </Alert>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <OrbitalLoader />
                </motion.div>
              ) : !hasContent ? (
                <motion.div key="empty" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}>
                  <Box sx={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", py: 12, textAlign: "center",
                  }}>
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Box sx={{
                        width: 88, height: 88, borderRadius: "50%", mb: 3,
                        background: isDark
                          ? "radial-gradient(circle, rgba(10,14,39,0.2), rgba(10,14,39,0.05))"
                          : "radial-gradient(circle, rgba(10,14,39,0.08), rgba(10,14,39,0.02))",
                        border: `2px solid ${isDark ? "rgba(10,14,39,0.35)" : "rgba(10,14,39,0.12)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isDark
                          ? "0 0 40px rgba(10,14,39,0.2)"
                          : "0 0 40px rgba(10,14,39,0.06)",
                      }}>
                        <InsertDriveFile sx={{ fontSize: 38, color: isDark ? "#334155" : "#cbd5e1" }} />
                      </Box>
                    </motion.div>

                    <Typography sx={{ fontFamily: "Michroma", fontSize: 20, fontWeight: 800,
                      color: isDark ? "#94a3b8" : "#475569", mb: 1.5 }}>
                      No reports here yet
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: textSub, maxWidth: 400, lineHeight: 1.8 }}>
                      {subPrefix
                        ? "This folder is empty."
                        : "Reports processed through Proove AI will appear here once uploaded to your archive."}
                    </Typography>
                    {subPrefix && (
                      <Button startIcon={<ArrowBack />} onClick={goUp}
                        sx={{
                          mt: 3, textTransform: "none", fontWeight: 700, borderRadius: "10px",
                          color: BRAND.navy, px: 3, py: 1,
                          bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.07)",
                          "&:hover": { bgcolor: isDark ? "rgba(10,14,39,0.25)" : "rgba(10,14,39,0.12)" },
                        }}>
                        Back
                      </Button>
                    )}
                  </Box>
                </motion.div>
              ) : (
                <motion.div key={subPrefix} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}>

                  {/* Folders section */}
                  {data.folders.length > 0 && (
                    <Box sx={{ mb: data.files.length > 0 ? 5 : 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                        <Typography sx={{
                          fontSize: 10.5, fontWeight: 800, letterSpacing: "0.16em", color: textSub,
                        }}>FOLDERS</Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: border }} />
                        <Chip label={data.folders.length} size="small" sx={{
                          height: 20, fontSize: 10.5, fontWeight: 700,
                          bgcolor: isDark ? "rgba(10,14,39,0.2)" : "rgba(10,14,39,0.08)",
                          border: `1px solid ${isDark ? "rgba(10,14,39,0.4)" : "rgba(10,14,39,0.15)"}`,
                          color: isDark ? BRAND.gold : BRAND.navy,
                        }} />
                      </Box>
                      {/* Vertically stacked, full-width rows — these folder
                          names (patient identity + DOB/NPI, report type,
                          date) run long, and a multi-column grid with a
                          248px minimum truncated them into unreadable
                          ellipses. One per row gives every name the full
                          card width to wrap into instead. */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {data.folders.map((folder, i) => (
                          <motion.div key={folder} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}>
                            <FolderCard name={folder} isDark={isDark} border={border}
                              onClick={() => openFolder(folder)} />
                          </motion.div>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Files section */}
                  {data.files.length > 0 && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                        <Typography sx={{
                          fontSize: 10.5, fontWeight: 800, letterSpacing: "0.16em", color: textSub,
                        }}>FILES</Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: border }} />
                        <Chip label={data.files.length} size="small" sx={{
                          height: 20, fontSize: 10.5, fontWeight: 700,
                          bgcolor: isDark ? "rgba(99,124,219,0.1)" : "rgba(99,124,219,0.12)",
                          border: `1px solid rgba(99,124,219,0.25)`,
                          color: BRAND.gold,
                        }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {data.files.map((file, i) => (
                          <motion.div key={file.blob_name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}>
                            <FileCard file={file} isDark={isDark} border={border}
                              onPreview={handlePreview} onDownload={handleDownload} />
                          </motion.div>
                        ))}
                      </Box>
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>

        {/* ── PDF Preview side panel ── */}
        <AnimatePresence>
          {previewFile && (
            <PreviewPanel
              file={previewFile}
              signedUrl={signedUrl}
              onClose={() => { setPreviewFile(null); setSignedUrl(""); }}
              isDark={isDark}
              border={border}
            />
          )}
        </AnimatePresence>
      </Box>

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </Box>
  );
}
